import { useMemo } from "react";
import { thumbUrl } from "../utils/imageUpload.js";
import { bogoLabel, bundleDiscount } from "../utils/helpers.js";
import { Icon } from "./Icon.jsx";
import { TAXONOMY } from "../config/taxonomy.js";

// ─── PROMO TILES ─────────────────────────────────────────────────────────────
// The bold, colour-blocked "ad" tiles big marketplaces use to pull people into the catalogue (Amazon deals, Flipkart
// shelves, IKEA blocks). Nothing here is invented: every tile is built from what is really in the catalogue or really
// set by a seller — an actual markdown (MRP above price), an actual Buy X Get Y offer, actual free-shipping terms, the
// actual price ceiling that most products fall under, the actual newest listings. A tile whose real data is missing is
// simply not shown, so there are never fake sales.
const PHOTO = (p) => (Array.isArray(p.images) && p.images[0]) || p.img || null;
const hasPhoto = (p) => !!PHOTO(p);
const pct = (p) => (Number(p.mrp) > Number(p.price) && Number(p.price) > 0 ? Math.round((1 - Number(p.price) / Number(p.mrp)) * 100) : 0);

// Palette: cobalt, butter yellow, a pale sky blue and a deep navy (all from the brand blue/yellow family) — no gradients, no
// black/white blocks. The tiles take their colour from their POSITION in the block so no two neighbours match, across
// the 4x2 grid (sideways and down) and in the single phone column, whatever data each tile is made from.
const SKIN = {
  blue:   { bg: "var(--teal)",   fg: "#FFFFFF", blend: "screen",   op: 0.16 },
  butter: { bg: "var(--butter)", fg: "#0B0B0B", blend: "multiply", op: 0.2 },
  sky:    { bg: "#CFDDFA",       fg: "#0B0B0B", blend: "multiply", op: 0.18 },
  navy:   { bg: "#16306E",       fg: "#FFFFFF", blend: "screen",   op: 0.14 },
};
const CYCLE = ["blue", "butter", "sky", "navy", "butter", "blue", "navy", "sky"];
// A store's own offer cards (StoreOffers) pick a skin by what the offer is.
const SKINS = { offer: SKIN.butter, under: SKIN.sky, sale: SKIN.blue, ship: SKIN.navy };

// A real photo per tile (Unsplash, bundled in /public/images) so the coloured tiles read as an ad, faded behind the live products.
const IMG = Object.fromEntries(["sale","offer","ship","under","under2","fresh","almost","store","premium","gifts","jewellery","apparel","home","bags","beauty"].map(k => [k, `/images/promo-${k}.jpg`]));

export function buildPromos(products) {
  const list = (products || []).filter(p => hasPhoto(p) && Number(p.price) > 0 && p.is_live !== false && !p.sold && Number(p.stock ?? 99) > 0);
  const out = [];
  const marked = list.filter(p => pct(p) > 0).sort((a, b) => pct(b) - pct(a));
  if (marked.length >= 2) out.push({ kind: "sale", title: "On sale", sub: `Up to ${pct(marked[0])}% off`, items: marked });
  const bogo = list.filter(p => p.bogo_eligible && bogoLabel(p._storeInfo));
  if (bogo.length >= 2) {
    const labels = [...new Set(bogo.map(p => bogoLabel(p._storeInfo)))];
    out.push({ kind: "offer", title: "Buy more, get more", sub: labels.length === 1 ? labels[0] : `${labels[0]} and more`, items: bogo });
  }
  const free = list.filter(p => Number(p._storeInfo?.shipping_cost) === 0);
  const freeStores = new Set(free.map(p => p._storeInfo?.id));
  if (free.length >= 2) out.push({ kind: "ship", title: "Free shipping", sub: `${freeStores.size} ${freeStores.size === 1 ? "store ships" : "stores ship"} free`, items: free });
  // Price bands: the smallest ceiling with at least 4 products, then the next one up with at least 4 more.
  const bands = [199, 299, 499, 999, 1999];
  let used = -1;
  for (const [kind, min] of [["under", 4], ["under2", 4]]) {
    for (let i = used + 1; i < bands.length; i++) {
      const t = bands[i];
      const inBand = list.filter(p => Number(p.price) <= t).sort((a, b) => Number(a.price) - Number(b.price));
      const prev = used >= 0 ? list.filter(p => Number(p.price) <= bands[used]).length : 0;
      if (inBand.length - prev >= min || (kind === "under" && inBand.length >= min)) { out.push({ kind, title: `Under ₹${t.toLocaleString("en-IN")}`, sub: `${inBand.length} picks`, items: inBand }); used = i; break; }
    }
  }
  const fresh = [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  if (fresh.length >= 2) out.push({ kind: "fresh", title: "Just landed", sub: "Newest from independent labels", items: fresh });
  // Real scarcity: products that genuinely have only a few units left.
  const almost = list.filter(p => Number(p.stock) > 0 && Number(p.stock) <= 3);
  if (almost.length >= 3) out.push({ kind: "almost", title: "Almost gone", sub: "Only a few left", items: almost });
  const gifts = list.filter(p => p.taxonomy_cat === "bundles");
  if (gifts.length >= 3) out.push({ kind: "gifts", title: "Gift ideas", sub: `${gifts.length} bundles and gifts`, items: gifts });
  // The store with the most products, and the priciest picks.
  const byStore = new Map();
  for (const p of list) { const st = p._storeInfo; if (st?.id) { if (!byStore.has(st.id)) byStore.set(st.id, { st, items: [] }); byStore.get(st.id).items.push(p); } }
  const top = [...byStore.values()].sort((a, b) => b.items.length - a.items.length)[0];
  if (top && top.items.length >= 4) out.push({ kind: "store", title: top.st.store_name, sub: `Shop all ${top.items.length} products`, items: top.items });
  const premium = list.filter(p => Number(p.price) >= 999).sort((a, b) => Number(b.price) - Number(a.price));
  if (premium.length >= 3) out.push({ kind: "premium", title: "Premium picks", sub: `From ₹${Number(premium[premium.length - 1].price).toLocaleString("en-IN")}`, items: premium });
  // One tile per top category that really has stock in it.
  const catTiles = ["jewellery", "apparel", "home", "bags", "beauty"].map(slug => {
    const items = list.filter(p => p.taxonomy_cat === slug);
    const c = TAXONOMY.find(x => x.slug === slug);
    return items.length >= 3 ? { kind: slug, title: c.label, sub: `${items.length} ${items.length === 1 ? "style" : "styles"}`, items } : null;
  }).filter(Boolean).sort((a, b) => b.items.length - a.items.length);
  out.push(...catTiles);
  // A full 4x2 block when there is enough real content, a single row of 4 otherwise.
  return out.length >= 8 ? out.slice(0, 8) : out.length >= 4 ? out.slice(0, 4) : out;
}

export function PromoTiles({ products, isMobile, onOpen }) {
  const all = useMemo(() => buildPromos(products), [products]);
  const promos = isMobile ? all.slice(0, 6) : all;
  if (promos.length < 2) return null;
  const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  return (
    <div style={isMobile
      ? { display: "flex", flexDirection: "column", gap: 34, padding: "0 16px" }
      : { display: "grid", gridTemplateColumns: `repeat(${Math.min(promos.length, 4)}, minmax(0, 1fr))`, gap: 28 }}>
      {promos.map((pr, idx) => {
        const sk = SKIN[CYCLE[idx % CYCLE.length]];
        const shown = pr.items.slice(0, 3);
        return (
          <div key={pr.kind} onClick={() => onOpen(pr.title, pr.items)}
            style={{ position: "relative", overflow: "hidden", width: "auto", background: sk.bg, color: sk.fg, border: sk.border ? "1px solid var(--border)" : "none", borderRadius: 4, padding: isMobile ? 22 : 24, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: isMobile ? 22 : 18, cursor: "pointer", minHeight: isMobile ? 0 : 290 }}>
            {/* the lifestyle photo sits behind the content as a soft, faded backdrop (blue and butter tiles only) */}
            {sk.blend && <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMG[pr.kind]})`, backgroundSize: "cover", backgroundPosition: "center", opacity: sk.op, mixBlendMode: sk.blend, pointerEvents: "none" }} />}
            <div style={{ position: "relative" }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(pr.title, pr.items); }}
                style={{ all: "unset", cursor: "pointer", display: "block", fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: isMobile ? 38 : 42, lineHeight: 0.92, letterSpacing: "-0.02em", textTransform: "uppercase", color: sk.fg }}>{pr.title}</button>
              <div style={{ fontSize: 15, fontWeight: 500, marginTop: isMobile ? 8 : 10 }}>{pr.sub}</div>
            </div>
            <div style={{ position: "relative", display: "grid", gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`, gap: isMobile ? 12 : 10 }}>
              {shown.map(p => {
                const off = pct(p);
                return (
                  <div key={p.id} style={{ background: "#fff", borderRadius: 4, overflow: "hidden", color: "#0B0B0B", border: sk.border ? "1px solid var(--border)" : "none" }}>
                    <div style={{ aspectRatio: isMobile ? "5 / 4" : "1 / 1", background: "var(--surface)", position: "relative" }}>
                      <img src={thumbUrl(PHOTO(p), 240)} alt={p.name} loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      {off > 0 && <span style={{ position: "absolute", left: 0, bottom: 4, background: "#0B0B0B", color: "#fff", fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: "0 2px 2px 0" }}>{off}%</span>}
                    </div>
                    <div style={{ padding: "5px 6px 6px", fontSize: 13, fontWeight: 700 }}>{money(p.price)}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 4, fontSize: 14, fontWeight: 700 }}>See all {pr.items.length} <Icon n="chevron-right" size={18} /></div>
          </div>
        );
      })}
    </div>
  );
}

// A store's own live offers as colour-blocked cards for its storefront. Only offers the seller has really switched on.
export function storeOfferCards(store) {
  if (!store) return [];
  const cards = [];
  const bogo = bogoLabel(store);
  if (bogo) cards.push({ kind: "offer", icon: "gift", title: bogo, sub: "On selected items" });
  const bo = store.bundle_offer;
  if (bo && bo.active !== false && Number(bo.min_qty) > 0 && Number(bo.price) > 0) cards.push({ kind: "under", icon: "bag", title: `${bo.min_qty} for ₹${Number(bo.price).toLocaleString("en-IN")}`, sub: "Bundle price" });
  const tiers = (Array.isArray(store.bulk_discounts) ? store.bulk_discounts : []).filter(d => d.active !== false).sort((a, b) => a.min_qty - b.min_qty);
  if (tiers.length) cards.push({ kind: "sale", icon: "percent", title: tiers.map(d => `Buy ${d.min_qty}+ → ${d.type === "percent" ? `${d.value}% off` : `₹${d.value} off`}`).join("  ·  "), sub: "Automatic at checkout" });
  if (Number(store.free_ship_min_amount) > 0) cards.push({ kind: "ship", icon: "truck", title: `Free shipping over ₹${Number(store.free_ship_min_amount).toLocaleString("en-IN")}`, sub: "Applied automatically" });
  else if (Number(store.shipping_cost) === 0 && store.dispatch_days) cards.push({ kind: "ship", icon: "truck", title: "Free shipping", sub: `Ships in ${store.dispatch_days}` });
  return cards;
}

export function StoreOffers({ store, isMobile }) {
  const cards = useMemo(() => storeOfferCards(store), [store]);
  if (!cards.length) return null;
  return (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", scrollbarWidth: "none", padding: isMobile ? "16px 4vw 0" : "24px 6vw 0" }}>
      {cards.map(c => {
        const sk = SKINS[c.kind];
        return (
          <div key={c.title} style={{ flex: "1 1 220px", minWidth: 220, background: sk.bg, color: sk.fg, borderRadius: 4, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ width: 40, height: 40, borderRadius: 4, background: "rgba(255,255,255,0.85)", color: "#1B1814", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon n={c.icon} size={22} /></span>
            <span><span style={{ display: "block", fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{c.title}</span><span style={{ display: "block", fontSize: 13, marginTop: 2, opacity: 0.9 }}>{c.sub}</span></span>
          </div>
        );
      })}
    </div>
  );
}
